import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Anchor, ListChecks, Search, TrendingUp, MessageSquare, FileSignature, LifeBuoy, Hand, BarChart3, BookOpen, ArrowRight, CheckCircle2, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";

const STEPS = [
  { icon: ListChecks, title: "Seçmen Listene git", desc: "Sol menüden \u201cSeçmen Listesi\u201d\u2019ne gir; sorumlu olduğun üyeleri gör.", route: "/secmenler" },
  { icon: Search, title: "Üyeni ara", desc: "Arama çubuğuna firma adı ya da sahip ismini yazarak hızlıca bul.", flag: "pred_or_note" },
  { icon: TrendingUp, title: "Seçim tercihini gir", desc: "Üyeyle görüş, 2026 tahminini işaretle: Bizde / Kararsız / Karşı.", flag: "prediction" },
  { icon: MessageSquare, title: "Arama notu ekle", desc: "Görüşme sonucunu not düş; herkes ne konuşulduğunu görsün, iz kalsın.", flag: "note" },
  { icon: FileSignature, title: "Oy bizdeyse yetki belgesini al", desc: "Kesinleşen üyeler için yetki belgesini hazırla ve takibe al.", flag: "certificate" },
  { icon: LifeBuoy, title: "Ulaşamadıysan / karşıysa / gelmeyecekse Havuza at", desc: "Üyeyi havuza bırak; onu tanıyan ya da daha yakın biri devralsın.", flag: "pool_add" },
  { icon: Hand, title: "Havuz sekmesini takip et", desc: "Tanıdığın firmalara \u201cİlgileniyorum\u201d de veya \u201cDevral\u201d ile üstlen.", flag: "pool_engage" },
  { icon: BarChart3, title: "Genel Panel'den ilerlemeni izle", desc: "Hedef, kesin, kalan ve sahipsiz sayılarını buradan takip et.", route: "/panel" },
  { icon: BookOpen, title: "Takıldığında Kullanım Kılavuzu'na bak", desc: "Her adımın detayı sol menüdeki \u201cKullanım Kılavuzu\u201d\u2019nda.", route: "/kilavuz" },
];

export default function OnboardingDialog({ userId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);
  const [progress, setProgress] = useState(null);
  const [visited, setVisited] = useState([]);
  const key = `imeak_onboarding_hidden_${userId}`;
  const seenKey = `imeak_onboarding_seen_${userId}`;

  useEffect(() => {
    if (!userId) return;
    let hidden = false;
    try {
      hidden = localStorage.getItem(key) === "1" || localStorage.getItem(seenKey) === "1";
    } catch { /* ignore */ }
    if (!hidden) {
      setOpen(true);
      try { localStorage.setItem(seenKey, "1"); } catch { /* ignore */ }
    }
  }, [userId, key, seenKey]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("imeak:onboarding", onOpen);
    return () => window.removeEventListener("imeak:onboarding", onOpen);
  }, []);

  useEffect(() => {
    if (!open || !userId) return;
    api.get("/onboarding/progress").then(({ data }) => setProgress(data)).catch(() => setProgress({}));
    try { setVisited(JSON.parse(localStorage.getItem(`imeak_visited_${userId}`) || "[]")); } catch { setVisited([]); }
  }, [open, userId]);

  const isDone = (s) => (s.route ? visited.includes(s.route) : !!(progress && progress[s.flag]));
  const doneCount = STEPS.filter(isDone).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);
  const allDone = progress !== null && doneCount === STEPS.length;
  const [celebrate, setCelebrate] = useState(false);
  const confetti = React.useMemo(() => Array.from({ length: 44 }, () => ({
    left: `${Math.random() * 100}%`,
    color: ["#06B6D4", "#0F2C59", "#10B981", "#F59E0B", "#EF4444"][Math.floor(Math.random() * 5)],
    dur: `${2 + Math.random() * 1.6}s`,
    delay: `${Math.random() * 0.6}s`,
  })), []);
  useEffect(() => {
    if (open && allDone) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 4500);
      return () => clearTimeout(t);
    }
  }, [open, allDone]);

  const close = () => {
    if (dontShow) { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } }
    setOpen(false);
  };
  const goStart = () => { close(); navigate("/secmenler"); };
  const resetHint = "Rehber yalnızca ilk girişte açılır; üstteki yardım ikonundan yeniden açabilirsiniz.";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="p-0 gap-0 overflow-hidden w-[calc(100vw-1.5rem)] max-w-lg rounded-2xl border-0" data-testid="onboarding-dialog">
        {celebrate && (
          <div className="pointer-events-none absolute inset-0 z-50 overflow-hidden" data-testid="onboarding-confetti">
            {confetti.map((c, i) => (
              <span key={i} className="confetti-piece" style={{ left: c.left, backgroundColor: c.color, animationDuration: c.dur, animationDelay: c.delay }} />
            ))}
          </div>
        )}
        {/* Poster header */}
        <div className="relative px-6 py-6 bg-[#0F2C59] text-white overflow-hidden">
          <div className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-cyan-500/30 blur-2xl" />
          <div className="absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-cyan-400/20 blur-2xl" />
          <div className="relative flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center shrink-0 backdrop-blur">
              <Anchor className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold">Başlangıç Rehberi</p>
              <DialogTitle className="font-display text-xl font-bold leading-tight text-white">Hoş geldin! Ne yapman gerekiyor?</DialogTitle>
            </div>
          </div>
          <DialogDescription className="relative mt-3 text-sm text-white/80">Aşağıdaki adımları sırayla izle. Her üye için akış aynı: bul, konuş, işaretle, sahiplen.</DialogDescription>
          <div className="relative mt-3" data-testid="onboarding-progress">
            <div className="flex items-center justify-between text-[11px] text-white/80 mb-1">
              <span>İlerlemen</span>
              <span data-testid="onboarding-progress-count"><b className="text-white">{doneCount}</b> / {STEPS.length} adım tamamlandı</span>
            </div>
            <div className="h-2 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full rounded-full bg-cyan-400 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {/* Steps */}
        {allDone && (
          <div className="mx-4 mt-4 rounded-xl border border-emerald-300 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-3 flex items-center gap-2.5" data-testid="onboarding-complete-banner">
            <PartyPopper className="h-6 w-6 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Tebrikler! Tüm adımları tamamladın.</p>
              <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80">Artık tam anlamıyla hazırsın. Sahada başarılar!</p>
            </div>
          </div>
        )}
        <ScrollArea className="max-h-[52vh]">
          <ol className="p-4 sm:p-5 space-y-2.5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = isDone(s);
              return (
                <li key={i} className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${done ? "border-emerald-300 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-950/20" : "border-border bg-card/60"}`} data-testid={`onboarding-step-${i + 1}`} data-done={done ? "1" : "0"}>
                  <div className="relative shrink-0">
                    <div className={`h-9 w-9 rounded-lg grid place-items-center ${done ? "bg-emerald-100 dark:bg-emerald-950/50" : "bg-cyan-50 dark:bg-cyan-950/40"}`}>
                      <Icon className={`h-5 w-5 ${done ? "text-emerald-600" : "text-cyan-600"}`} />
                    </div>
                    {done ? (
                      <CheckCircle2 className="absolute -top-1.5 -left-1.5 h-5 w-5 text-emerald-600 bg-white dark:bg-card rounded-full" data-testid={`onboarding-step-done-${i + 1}`} />
                    ) : (
                      <span className="absolute -top-1.5 -left-1.5 h-5 w-5 rounded-full bg-[#0F2C59] text-white text-[10px] font-bold grid place-items-center">{i + 1}</span>
                    )}
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <p className={`text-sm font-semibold leading-snug ${done ? "text-emerald-700 dark:text-emerald-400" : "text-foreground"}`}>{s.title}</p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">{s.desc}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </ScrollArea>

        {/* Footer */}
        <div className="border-t border-border p-4 flex flex-col sm:flex-row items-center gap-3 bg-card">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none mr-auto" title={resetHint} data-testid="onboarding-dontshow-label">
            <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(!!v)} data-testid="onboarding-dontshow" />
            Bir daha gösterme
          </label>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" className="flex-1 sm:flex-none" onClick={close} data-testid="onboarding-close">Kapat</Button>
            <Button className="flex-1 sm:flex-none bg-[#0F2C59] hover:bg-[#0F2C59]/90" onClick={goStart} data-testid="onboarding-start">
              Seçmen Listeme Git <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
